import { countWords } from '../count-words';
import { showConversationGroupsToolSchema, showConversationToolSchema, showDiagnosisToolSchema } from './diagnosis';
import { saveTestCasesToolSchema } from './test-cases';
import { askUserToolSchema, proposePlanToolSchema } from './widget-schemas';
// How much one reply puts in front of the user. The evals and the app count it the same way, so a number seen in
// production means what it means in a test.
/** About the pace an adult reads plain non-fiction at. */
const READING_WORDS_PER_MINUTE = 240;
const NOTHING_SHOWN = { texts: [] };
// A call still streaming, or one the model got wrong, has arguments that don't parse; its card shows nothing yet.
const reader = (schema, shown) => (args) => {
    try {
        const parsed = schema.safeParse(JSON.parse(args || '{}'));
        return parsed.success ? shown(parsed.data) : NOTHING_SHOWN;
    }
    catch {
        return NOTHING_SHOWN;
    }
};
// A card shows its questions one at a time, and every one of them is there to read before the user is done with it.
const questionTexts = (questions) => questions.flatMap(({ header, question, options }) => [
    header,
    question,
    ...options.flatMap(({ label, description }) => [label, description])
]);
// The cards whose words Via writes, and which show them without a click. Every other card draws the app's own data.
const CARD_READERS = {
    [proposePlanToolSchema.name]: reader(proposePlanToolSchema.inputSchema, ({ title, summary, steps, questions = [] }) => ({
        texts: [
            title,
            summary,
            ...steps.flatMap((step) => [step.title, step.detail]),
            ...questionTexts(questions)
        ],
        questions: questions.length,
        planSteps: steps.length
    })),
    [askUserToolSchema.name]: reader(askUserToolSchema.inputSchema, ({ questions }) => ({
        texts: questionTexts(questions),
        questions: questions.length
    })),
    [saveTestCasesToolSchema.name]: reader(saveTestCasesToolSchema.inputSchema, ({ tests }) => ({
        texts: tests.flatMap(({ name, expects }) => [name, 'reply' in expects ? expects.reply : expects.action])
    })),
    [showDiagnosisToolSchema.name]: reader(showDiagnosisToolSchema.inputSchema, ({ question, evidence, cause, fix }) => ({
        texts: [question, ...evidence.map(({ text }) => text), cause, fix]
    })),
    [showConversationToolSchema.name]: reader(showConversationToolSchema.inputSchema, ({ title, moments }) => ({
        texts: [title, ...moments.map(({ note }) => note)]
    })),
    [showConversationGroupsToolSchema.name]: reader(showConversationGroupsToolSchema.inputSchema, ({ title, period, groups }) => ({
        texts: [title, period, ...groups.flatMap(({ label, detail }) => [label, detail])]
    }))
};
export const measureViaReply = ({ text, toolCalls }) => {
    const cards = toolCalls.flatMap(({ name, args }) => CARD_READERS[name]?.(args) ?? []);
    const words = countWords(text);
    const cardWords = countWords(cards.flatMap(({ texts }) => texts).join(' '));
    return {
        words,
        cardWords,
        questions: cards.reduce((sum, { questions = 0 }) => sum + questions, 0),
        planSteps: cards.reduce((sum, { planSteps = 0 }) => sum + planSteps, 0),
        steps: toolCalls.length,
        readSeconds: Math.round(((words + cardWords) / READING_WORDS_PER_MINUTE) * 60)
    };
};
//# sourceMappingURL=reply-size.js.map